/** Abas da lista de conversas. Os valores viajam na query string do endpoint de conversas. */
export enum ConversationView {
  MY_CONVERSATIONS = "my",
  AI_AGENTS = "ai",
}

/** Filtros de status disponíveis na aba "Minhas Conversas". */
export enum MyConversationsFilter {
  /** Abertas em que o cliente falou por último e ainda aguarda resposta. */
  UNREAD = "unread",
  /** Todas as abertas. */
  OPEN = "open",
  /** Abertas que já respondemos, então a bola está com o cliente. */
  AWAITING_CLIENT = "awaiting",
  /** Conversas já finalizadas. */
  CLOSED = "closed",
}

/** Filtros de status disponíveis na aba "Agentes de IA". */
export enum AIAgentsFilter {
  /** Abertas sem atendente humano, sendo conduzidas pelo workflow. */
  ATTENDING = "attending",
  CLOSED = "closed",
}

export type ConversationFilter = MyConversationsFilter | AIAgentsFilter;

export const DEFAULT_FILTER_BY_VIEW: Record<ConversationView, ConversationFilter> = {
  [ConversationView.MY_CONVERSATIONS]: MyConversationsFilter.UNREAD,
  [ConversationView.AI_AGENTS]: AIAgentsFilter.ATTENDING,
};

export type InboxPlacementSource = {
  userId?: number | null;
  endDate?: string | Date | null;
  lastReceivedDate?: string | Date | null;
  lastInterationDate?: string | Date | null;
};

const toTime = (value: string | Date | null | undefined) => {
  if (!value) return NaN;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : NaN;
};

/** Same tab/status rules as GET conversation/all/{platformId}/filtered. */
export function resolveInboxPlacement(source: InboxPlacementSource): {
  view: ConversationView;
  filter: ConversationFilter;
} {
  const view =
    Number(source.userId) > 0 ? ConversationView.MY_CONVERSATIONS : ConversationView.AI_AGENTS;
  const closed = Boolean(source.endDate);

  if (closed) {
    return {
      view,
      filter: view === ConversationView.AI_AGENTS ? AIAgentsFilter.CLOSED : MyConversationsFilter.CLOSED,
    };
  }

  if (view === ConversationView.AI_AGENTS) {
    return { view, filter: AIAgentsFilter.ATTENDING };
  }

  const received = toTime(source.lastReceivedDate);
  const interaction = toTime(source.lastInterationDate);
  const awaitingAnswer = Number.isFinite(received) && (!Number.isFinite(interaction) || received > interaction);

  return {
    view,
    filter: awaitingAnswer ? MyConversationsFilter.UNREAD : MyConversationsFilter.AWAITING_CLIENT,
  };
}
